import { createStore, useSelector } from '@tanstack/react-store'
import { initialEnrollmentSnapshot } from './enrollmentDefaults'
import { clearPersistedEnrollmentSnapshot } from './enrollmentPersistence'
import type {
  EnrollmentFormData,
  EnrollmentPath,
  EnrollmentSnapshot,
  EnrollmentState,
  UploadedDocumentFiles
} from './enrollmentState.types'

const updateEnrollmentStore = (updater: (state: EnrollmentState) => EnrollmentState) => {
  enrollmentStore.setState(updater)
}

const createEnrollmentState = (snapshot: EnrollmentSnapshot): EnrollmentState => ({
  ...snapshot,
  userProfile: null,
  resumedEnrollment: null,
  setEnrollmentId: (id: number | null) =>
    updateEnrollmentStore(state => ({ ...state, enrollmentId: id })),
  setSessionToken: (token: string | null) =>
    updateEnrollmentStore(state => ({ ...state, sessionToken: token })),
  setCurrentStep: (step: number) =>
    updateEnrollmentStore(state => ({ ...state, currentStep: step })),
  setEnrollmentPath: (path: EnrollmentPath | null) =>
    updateEnrollmentStore(state => ({ ...state, enrollmentPath: path })),
  setLocationId: (id: number | null) =>
    updateEnrollmentStore(state => ({ ...state, locationId: id })),
  setProgramId: (id: number | null) =>
    updateEnrollmentStore(state => ({ ...state, programId: id })),
  setStartDateId: (id: number | null) =>
    updateEnrollmentStore(state => ({ ...state, startDateId: id })),
  updateFormData: (data: Partial<EnrollmentFormData>) =>
    updateEnrollmentStore(state => ({
      ...state,
      formData: { ...state.formData, ...data }
    })),
  updateUploadedDocumentFiles: (data: Partial<UploadedDocumentFiles>) =>
    updateEnrollmentStore(state => ({
      ...state,
      uploadedDocumentFiles: { ...state.uploadedDocumentFiles, ...data }
    })),
  setAssessmentId: (id: number | null) =>
    updateEnrollmentStore(state => ({ ...state, assessmentId: id })),
  setSignatureCompleted: (completed: boolean) =>
    updateEnrollmentStore(state => ({ ...state, signatureCompleted: completed })),
  setEnrollmentCompleted: (completed: boolean) =>
    updateEnrollmentStore(state => ({ ...state, enrollmentCompleted: completed })),
  setUserProfile: (profile) =>
    updateEnrollmentStore(state => ({ ...state, userProfile: profile })),
  setResumedEnrollment: (enrollment) =>
    updateEnrollmentStore(state => ({ ...state, resumedEnrollment: enrollment })),
  resetEnrollment: () => {
    updateEnrollmentStore(() => createEnrollmentState(initialEnrollmentSnapshot))
  }
})

export const enrollmentStore = createStore<EnrollmentState>(
  createEnrollmentState(initialEnrollmentSnapshot)
)

type UseEnrollmentStore = {
  (): EnrollmentState
  <TSelected>(selector: (state: EnrollmentState) => TSelected): TSelected
  getState: () => EnrollmentState
  setState: (updater: EnrollmentState | ((state: EnrollmentState) => EnrollmentState)) => void
}

const useEnrollmentStoreSelector = <TSelected,>(
  selector?: (state: EnrollmentState) => TSelected
) => useSelector(enrollmentStore, selector)

export const useEnrollmentStore = Object.assign(useEnrollmentStoreSelector, {
  getState: () => enrollmentStore.state,
  setState: (updater: EnrollmentState | ((state: EnrollmentState) => EnrollmentState)) => {
    if (typeof updater === 'function') {
      enrollmentStore.setState(updater)
      return
    }

    enrollmentStore.setState(() => updater)
  }
}) as UseEnrollmentStore

export const clearEnrollmentStore = () => {
  enrollmentStore.setState(() => createEnrollmentState(initialEnrollmentSnapshot))
  clearPersistedEnrollmentSnapshot()
}
